'use client';

/**
 * HeaderHome — Wrapper de HeaderFacelift para a home / (Figma 81:70).
 *
 * Conecta:
 *   - Nav ativo "Capa" (rota /)
 *   - CTA "Seja parceiro" → /parceiros (placeholder até a rota existir)
 *   - Dropdown de idioma → setLocaleAction (Server Action)
 *   - Locale tracking (AGR-2026-013) via trackLocaleToggled
 *
 * Comportamento sticky/transparente/hide-on-scroll já vive no HeaderFacelift.
 */
import * as React from 'react';
import { useTransition } from 'react';
import {
  HeaderFacelift,
  type HeaderLanguageOption,
  type HeaderNavItem,
} from '@brisa/ui/components/facelift/header/HeaderFacelift';
import {
  trackLocaleToggled,
  type BaseLandingEventProps,
} from '@brisa/shared/observability/landing';
import { setLocaleAction } from '../_actions/set-locale';

export interface HeaderHomeProps extends BaseLandingEventProps {
  currentLocale: 'pt' | 'es';
  currentPath: string;
  labels: {
    capa: string;
    clubes: string;
    planos: string;
    sejaParceiro: string;
  };
}

const LANGUAGES: HeaderLanguageOption[] = [
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
];

export function HeaderHome({
  currentLocale,
  currentPath,
  labels,
  user_id,
  session_id,
  feature_flag_active,
  feature_landing_planos_active,
}: HeaderHomeProps) {
  const [, startTransition] = useTransition();

  const nav: HeaderNavItem[] = React.useMemo(
    () => [
      { href: '/', label: labels.capa, active: currentPath === '/' },
      { href: '/clubes', label: labels.clubes, active: currentPath.startsWith('/clubes') },
      { href: '/pass', label: labels.planos, active: currentPath.startsWith('/pass') },
    ],
    [labels, currentPath],
  );

  const handleLanguageChange = (code: string) => {
    if (code === currentLocale) return;
    const newLocale: 'pt' | 'es' = code === 'es' ? 'es' : 'pt';
    trackLocaleToggled(
      { from_locale: currentLocale, to_locale: newLocale, path: currentPath },
      { user_id, session_id, feature_flag_active, feature_landing_planos_active },
    );
    startTransition(() => {
      void setLocaleAction(newLocale, currentPath);
    });
  };

  return (
    <HeaderFacelift
      nav={nav}
      ctaLink={{ href: '/parceiros', label: labels.sejaParceiro }}
      languages={LANGUAGES}
      currentLanguage={currentLocale}
      onLanguageChange={handleLanguageChange}
    />
  );
}
